import React, { useEffect, useState, useMemo } from "react";
import Plot from "react-plotly.js";

/**
 * GroupedResidueBarChart
 * ──────────────────────
 * Reusable per-residue bar chart that shows *two* overlaid groups side by
 * side: the full set (light color) and a highlighted subset (strong color).
 * Used for Contacts/Degree, Betweenness centrality, or any other per-residue
 * metric where you want to emphasize a top-N% subset.
 *
 * Props
 *   data            Array<{ position, value, isHighlighted? }>
 *                   - `position`: string residue label
 *                   - `value`: number for the bar height
 *                   - `isHighlighted`: optional bool; if omitted for every
 *                     row, the component derives it from top-N% of `value`
 *                     using the `percentile` prop.
 *   percentile      Number (default 10). Used only when rows lack
 *                   `isHighlighted`.
 *   title           Chart title.
 *   yAxisLabel      Label for the Y axis (e.g. "# of contacts").
 *   allLabel        Legend label for the total group (default "Total").
 *   highlightLabel  Legend label for the highlighted group
 *                   (default "Top N% hubs" interpolating percentile).
 *   baseColor       Hex for the light bars (default teal #9dd9d2).
 *   highlightColor  Hex for the highlighted bars (default teal dark #1e9e8a).
 *   onResidueClick  (position) => void on bar click.
 */
const GroupedResidueBarChart = ({
    data,
    percentile = 10,
    title = "",
    yAxisLabel = "Value",
    allLabel = "Total",
    highlightLabel,
    baseColor = "#9dd9d2",
    highlightColor = "#1e9e8a",
    onResidueClick,
}) => {
    const [layout, setLayout] = useState({});

    const { xPositions, yAll, yHighlight, highlightCount } = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return { xPositions: [], yAll: [], yHighlight: [], highlightCount: 0 };
        }

        // Sort by numeric residue position when possible
        const sorted = [...data].sort((a, b) => {
            const na = parseInt(String(a.position).split("_").pop(), 10);
            const nb = parseInt(String(b.position).split("_").pop(), 10);
            if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
            return String(a.position).localeCompare(String(b.position));
        });

        // Derive isHighlighted if not provided
        const anyExplicit = sorted.some((d) => typeof d.isHighlighted === "boolean");
        let flags;
        if (anyExplicit) {
            flags = sorted.map((d) => !!d.isHighlighted);
        } else {
            const vsorted = [...sorted].map((d) => d.value || 0).sort((a, b) => b - a);
            const cutoffIdx = Math.max(1, Math.floor((vsorted.length * percentile) / 100));
            const threshold = vsorted[Math.min(cutoffIdx - 1, vsorted.length - 1)];
            flags = sorted.map((d) => (d.value || 0) >= threshold);
        }

        return {
            xPositions: sorted.map((d) => d.position),
            // Each residue belongs to exactly ONE trace: highlighted residues
            // show only the highlight bar (null in yAll), non-highlighted
            // residues show only the base bar (null in yHighlight). This
            // avoids the double-bar (grouped) look at hub positions.
            yAll: sorted.map((d, i) => (flags[i] ? null : d.value || 0)),
            yHighlight: sorted.map((d, i) => (flags[i] ? d.value || 0 : null)),
            highlightCount: flags.filter(Boolean).length,
        };
    }, [data, percentile]);

    const plotData = useMemo(() => {
        if (xPositions.length === 0) return [];

        const hlLabel = highlightLabel ?? `Top ${percentile}% hubs`;

        return [
            {
                x: xPositions,
                y: yAll,
                type: "bar",
                name: allLabel,
                marker: { color: baseColor, line: { width: 0 } },
                hovertemplate:
                    `Residue %{x}<br>${allLabel}: %{y}<extra></extra>`,
            },
            {
                x: xPositions,
                y: yHighlight,
                type: "bar",
                name: hlLabel,
                marker: { color: highlightColor, line: { width: 0 } },
                hovertemplate:
                    `Residue %{x}<br>${hlLabel}: %{y}<extra></extra>`,
            },
        ];
    }, [
        xPositions,
        yAll,
        yHighlight,
        percentile,
        allLabel,
        highlightLabel,
        baseColor,
        highlightColor,
    ]);

    useEffect(() => {
        setLayout({
            autosize: true,
            title: { text: title },
            xaxis: { type: "category", title: { text: "Residue position" } },
            yaxis: { title: { text: yAxisLabel } },
            margin: { t: 50, b: 120, l: 60, r: 30 },
            showlegend: true,
            legend: { orientation: "h", y: 1.12, x: 1, xanchor: "right" },
            // Each residue is in exactly one trace (base OR highlight),
            // so overlay mode keeps each bar at the full category width.
            barmode: "overlay",
            bargap: 0.15,
        });
    }, [title, yAxisLabel]);

    const handleClick = (ev) => {
        if (!onResidueClick) return;
        const pt = ev?.points?.[0];
        if (pt && pt.x) onResidueClick(pt.x);
    };

    return (
        <div className="aspect-square">
            <Plot
                data={plotData}
                layout={layout}
                useResizeHandler
                onClick={handleClick}
                style={{ width: "100%", height: "100%" }}
                config={{ displaylogo: false, responsive: true }}
            />
            {highlightCount > 0 && (
                <div className="px-3 pb-2 text-secondary small">
                    {highlightCount} highlighted residue
                    {highlightCount === 1 ? "" : "s"}.
                </div>
            )}
        </div>
    );
};

export default GroupedResidueBarChart;
